import React from 'react';
import { isNil } from 'lodash-es';

import {
  Form,
  Message,
  Header,
  Segment,
  Label,
  InputOnChangeData,
  DropdownProps,
} from 'semantic-ui-react';

import {
  EvseController,
  EvseControllerConnector,
  EvseControllerConnectorStatus,
  EvseControllerConnectivityState,
} from 'types/evse-controller';
import { getEvseIdError } from 'utils/evse-ids';
import { validateConnectorId } from 'utils/connector';

type Props = {
  evseController: EvseController;
  connector: EvseControllerConnector;
  connectorStatus: EvseControllerConnectorStatus;
  validOptions: any;
  hideEvseId: boolean;
  onFieldChange: (name: string, value: any) => void;
  allowEditingEvseId: boolean;
};

export function ConfigureConnector({
  evseController,
  connector: {
    evseId = '',
    connectorId,
    standard,
    format,
    powerType,
    maxVoltage,
    maxAmperage,
  },
  connectorStatus,
  validOptions,
  hideEvseId,
  onFieldChange,
  allowEditingEvseId,
}: Props) {
  const connectorIdOptions = Object.keys(connectorStatus || {}).map((id) => ({
    key: id,
    value: parseInt(id, 10),
    text: `Connector ${id}`,
  }));

  const getNumberMessage = (
    options: { min?: number; max?: number } = {},
    label: string,
    value?: number
  ) => {
    if (isNil(options?.min) || isNil(options?.max)) return null;
    const inRange = value && options.min <= value && value <= options.max;
    const msg = [
      `${label} should be between ${options.min} and ${options.max}`,
    ];
    return <Message error={!inRange && !isNil(value)}>{msg}</Message>;
  };

  const getConnectorIdError = (connectorId?: number) => {
    try {
      validateConnectorId(connectorId);
    } catch (error: any) {
      return { content: error.message, pointing: 'above' };
    }

    return null;
  };

  const handleChange = (
    _: any,
    { name, value }: InputOnChangeData | DropdownProps
  ) => {
    if (name === 'evseId') {
      onFieldChange('evseIdMode', 'custom');
    }
    onFieldChange(name, value);
  };

  const isPendingFirstConnection =
    evseController.connectivityState ===
    EvseControllerConnectivityState.PendingFirstConnection;

  return (
    <div style={{ marginBottom: '20px' }}>
      <Header as="h3" content={`Connector ${connectorId ?? ''}`} />
      <Segment>
        {!hideEvseId &&
          (allowEditingEvseId ? (
            <Form.Input
              value={evseId ?? ''}
              name="evseId"
              label="Connector EVSE ID"
              type="text"
              onChange={handleChange}
              error={getEvseIdError(evseId)}
            />
          ) : (
            <Form.Input
              value={evseId ?? ''}
              name="evseId"
              label="Connector EVSE ID"
              readOnly
              type="text">
              <input style={{ backgroundColor: 'rgba(192,192,192,.25)' }} />
            </Form.Input>
          ))}

        {isPendingFirstConnection ? (
          <Form.Input
            value={connectorId}
            name="connectorId"
            label="Connector ID"
            type="number"
            onChange={(_, data) => {
              const value = data.value ? Number(data.value) : undefined;
              handleChange(_, { name: data.name, value });
            }}
            error={getConnectorIdError(connectorId)}
          />
        ) : (
          <Form.Select
            value={connectorId}
            options={connectorIdOptions}
            name="connectorId"
            label="Connector ID"
            type="text"
            onChange={handleChange}
          />
        )}

        <Form.Select
          value={powerType}
          options={validOptions.powerType}
          required
          name="powerType"
          label="Power Type"
          type="text"
          onChange={handleChange}
        />

        <Form.Select
          value={standard}
          options={validOptions.standard}
          name="standard"
          label="Connector Type / Standard"
          type="text"
          onChange={handleChange}
        />

        <Form.Select
          value={format}
          options={validOptions.format}
          name="format"
          label="Format"
          type="text"
          onChange={handleChange}
        />

        <Form.Input
          label="Max Voltage"
          name="maxVoltage"
          style={{ marginBottom: '4px' }}
          labelPosition="right"
          type="text"
          fluid
          value={maxVoltage}
          onChange={handleChange}>
          <input />
          <Label basic>V</Label>
        </Form.Input>
        {getNumberMessage(validOptions.maxVoltage, 'Max Voltage', maxVoltage)}

        <Form.Input
          label="Max Amperage"
          name="maxAmperage"
          style={{ marginBottom: '4px' }}
          labelPosition="right"
          type="text"
          fluid
          value={maxAmperage}
          onChange={handleChange}>
          <input />
          <Label basic>A</Label>
        </Form.Input>
        {getNumberMessage(
          validOptions.maxAmperage,
          'Max Amperage',
          maxAmperage
        )}
      </Segment>
    </div>
  );
}
